import { useState } from 'react';
import { Icon } from './Icon';
import { useUnsavedGuard } from './unsavedGuard';

export interface ModalDanger {
  label: string; // resting label, e.g. "Delete" / "Archive" / "Remove leave"
  // Immediate action (no confirm step) — e.g. owner Archive/Restore.
  onClick?: () => void;
  // Two-step inline confirm — e.g. Delete booking / Remove helper. When present,
  // the resting button swaps the footer for a confirm row.
  confirm?: { message: string; confirmLabel: string; onConfirm: () => void };
  // Shown but inert — the button stays where it always is, greyed, with the
  // reason on hover (Freddi, 2026-10-10: "keep the same but disable").
  disabled?: boolean;
  disabledHint?: string;
}

/**
 * Build a `blockers` list from conditional reasons, dropping the ones that do
 * not apply.
 *
 * Every pane wants to write `cond && 'why not'` per precondition and hand the
 * survivors to the footer. Without this they each repeated the same
 * `.filter((m): m is string => !!m)` narrowing — a type predicate copied four
 * ways, which is exactly the kind of line that gets one of them subtly wrong.
 */
export function saveBlockers(...reasons: (string | false | null | undefined)[]): string[] {
  return reasons.filter((r): r is string => !!r);
}

export interface ModalFooterProps {
  onCancel: () => void;
  onSave: () => void;
  saveLabel: string; // "Save" / "Create Booking" / "Log leave" …
  // WHY Save cannot be pressed — the reasons, in the words the user should read.
  // Non-empty disables the button AND shows the first reason beside it.
  //
  // A BARE BOOLEAN WAS THE BUG (Freddi, 2026-08-15). `saveDisabled` greyed the
  // button out and said nothing, in every pane — and the booking pane alone can
  // refuse for four distinct reasons. Someone who had done something perfectly
  // ordinary was left looking at a dead control, with the explanation, where one
  // existed at all, buried in a `title` on a wrapper span the width of the modal
  // away. That is how a payment typed in error came to be reported as a broken
  // Save button rather than as the rule it actually hit.
  //
  // There is deliberately NO boolean form left. Offering both would let a pane
  // go on disabling Save silently, which is the whole defect — and every pane
  // that used one turned out to have a perfectly sayable reason behind it.
  blockers?: readonly string[];
  danger?: ModalDanger | null; // omitted on create flows (no destructive action)
  // Keep open after saving — G14. Beside the destructive action and away from
  // Save, so it is never pressed by accident. Omitted on panes with no record
  // to keep open (a viewing, leave).
  keepOpen?: { on: boolean; onChange: (on: boolean) => void } | null;
}

// The shared pane footer: a destructive action on the left (immediate or inline-
// confirm) and Cancel / Save on the right. Source of truth = the booking/owner
// footers, promoted to a global so all four panes share the confirm behaviour.
export function ModalFooter({ onCancel, onSave, saveLabel, blockers, danger, keepOpen }: ModalFooterProps) {
  const [confirming, setConfirming] = useState(false);
  // Cancel is leaving without saving, which is exactly what the unsaved-changes
  // guard asks about — so it asks here, once, for every pane with a footer.
  const { guard } = useUnsavedGuard();
  // THE FIRST reason, not all of them. An empty form fails several preconditions
  // at once, and listing them reads as a report on the user rather than as the
  // next thing to do.
  const blocker = blockers?.[0] ?? null;

  if (confirming && danger?.confirm) {
    return (
      <div className="modal-foot">
        <span style={{ fontSize: 'var(--ts)', color: 'var(--alert-deep)' }}>{danger.confirm.message}</span>
        <div className="foot-right">
          <button type="button" className="btn" onClick={() => setConfirming(false)}>Cancel</button>
          <button type="button" className="btn danger" onClick={() => { setConfirming(false); danger.confirm!.onConfirm(); }}>{danger.confirm.confirmLabel}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-foot">
      {danger ? (
        <button type="button" className="btn danger" disabled={danger.disabled} title={danger.disabled ? danger.disabledHint : undefined} onClick={() => (danger.confirm ? setConfirming(true) : danger.onClick?.())}>{danger.label}</button>
      ) : (
        <span />
      )}
      {keepOpen && (
        <label className={'keep-open' + (keepOpen.on ? ' on' : '')}>
          <input type="checkbox" className="sr-only" checked={keepOpen.on} onChange={(e) => keepOpen.onChange(e.target.checked)} />
          <span className="box" aria-hidden="true">{keepOpen.on && <Icon name="check" size={12} />}</span>
          Keep open after saving
        </label>
      )}
      {/* Announced, not merely tinted: the message appears at the moment a
          control the user has just reached for stops working, and a disabled
          button is precisely the thing that cannot carry its own tooltip. */}
      {blocker && <span className="foot-blocker" role="status">{blocker}</span>}
      <div className="foot-right">
        <button type="button" className="btn" onClick={() => guard(onCancel)}>Cancel</button>
        <button type="button" className="btn primary" disabled={!!blocker} onClick={onSave}>{saveLabel}</button>
      </div>
    </div>
  );
}
